import { useEffect, useState } from "react";
import AdminLayout from "@/pages/admin/AdminLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, X, Trash2 } from "lucide-react";

const emptyForm = { slug: "", name: "", description: "", product_slugs: [], ribbon_text: "", status: "active", display_order: 100 };

// Marketing ribbon presets. "Custom" reveals the free-text field.
const RIBBON_PRESETS = [
  "Most Popular",
  "Best Value",
  "Wholesaler Favorite",
  "Recommended for New Brands",
  "Complete Sample Kit",
  "Premium Selection",
  "Staff Pick",
  "New",
  "Limited Availability",
];

const AdminSampleKits = () => {
  const [kits, setKits] = useState([]);
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const load = () => {
    api.get("/admin/sample-kits").then((r) => setKits(r.data.kits || [])).catch(() => toast.error("Failed to load kits"));
    api.get("/admin/products/all").catch(() => api.get("/products/all")).then((r) => setProducts(r.data.products || [])).catch(() => {});
  };

  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    if (form.product_slugs.length === 0) return toast.error("Select at least one product");
    try {
      if (editing) {
        const p = { ...form }; delete p.slug;
        await api.patch(`/admin/sample-kits/${editing.id}`, p);
      } else {
        await api.post("/admin/sample-kits", form);
      }
      toast.success("Saved");
      setEditing(null); setShowNew(false); setForm(emptyForm);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
  };

  const remove = async (k) => {
    if (!window.confirm(`Delete kit "${k.name}"?`)) return;
    try {
      await api.delete(`/admin/sample-kits/${k.id}`);
      toast.success("Deleted"); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Delete failed"); }
  };

  const toggleProduct = (slug) => {
    setForm((f) => ({ ...f, product_slugs: f.product_slugs.includes(slug) ? f.product_slugs.filter((s) => s !== slug) : [...f.product_slugs, slug] }));
  };

  const startEdit = (k) => {
    setEditing(k);
    setForm({ slug: k.slug, name: k.name, description: k.description || "", product_slugs: k.product_slugs || [], ribbon_text: k.ribbon_text || "", status: k.status, display_order: k.display_order });
  };

  return (
    <AdminLayout>
      <div className="flex justify-between items-start mb-8 flex-wrap gap-4">
        <div>
          <div className="ft-eyebrow mb-2">Sample Kits</div>
          <h1 className="text-3xl font-bold tracking-tight text-[var(--ft-black)]">Sample Kit Builder</h1>
          <p className="text-[var(--ft-muted)] mt-2 max-w-2xl">
            Build configurable sample kits from any combination of products. Public visitors on the Sample
            Request Center see the active kits as one-click bundles.
          </p>
        </div>
        <button onClick={() => { setForm(emptyForm); setShowNew(true); }} className="ft-btn-primary" data-testid="new-kit-btn"><Plus size={16}/> New Kit</button>
      </div>

      <div className="ft-card p-0 overflow-hidden">
        <table className="ft-table" data-testid="kits-table">
          <thead><tr><th>Kit</th><th>Products</th><th>Status</th><th>Order</th><th></th></tr></thead>
          <tbody>
            {kits.length === 0 && <tr><td colSpan={5} className="text-center text-[var(--ft-muted)] py-12">No kits yet.</td></tr>}
            {kits.map((k) => (
              <tr key={k.id} data-testid={`kit-row-${k.slug}`}>
                <td>
                  <div className="font-semibold text-[var(--ft-black)]">{k.name}</div>
                  <div className="text-xs font-mono text-[var(--ft-muted)]">{k.slug}</div>
                </td>
                <td className="text-xs">
                  {(k.product_slugs || []).length} product{k.product_slugs?.length === 1 ? "" : "s"}
                </td>
                <td>
                  <span className={`text-[0.625rem] font-bold uppercase tracking-[0.18em] px-2 py-1 rounded-full ${k.status === "active" ? "bg-[var(--ft-gold)] text-[var(--ft-black)]" : "bg-[var(--ft-charcoal)] text-white/80"}`}>{k.status}</span>
                </td>
                <td className="text-xs">{k.display_order}</td>
                <td>
                  <div className="flex gap-2">
                    <button onClick={() => startEdit(k)} className="text-xs px-3 py-1.5 rounded-full border border-[var(--ft-border)] hover:border-[var(--ft-gold)] hover:text-[var(--ft-gold)]" data-testid={`kit-edit-${k.slug}`}>Edit</button>
                    <button onClick={() => remove(k)} className="p-1.5 rounded-full text-[var(--ft-muted)] hover:text-[var(--ft-error)]" data-testid={`kit-del-${k.slug}`}><Trash2 size={14}/></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {(editing || showNew) && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => { setEditing(null); setShowNew(false); }}>
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-6">
              <h2 className="text-xl font-bold tracking-tight">{editing ? "Edit Kit" : "New Kit"}</h2>
              <button onClick={() => { setEditing(null); setShowNew(false); }}><X size={20}/></button>
            </div>
            <form onSubmit={save} className="space-y-4" data-testid="kit-form">
              <div className="grid md:grid-cols-2 gap-4">
                <div><label className="ft-label">Name*</label><input required className="ft-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="kit-name" /></div>
                <div><label className="ft-label">Slug*</label><input required disabled={!!editing} className="ft-input font-mono text-sm disabled:opacity-70" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") })} data-testid="kit-slug" /></div>
              </div>
              <div><label className="ft-label">Description</label><textarea rows={2} className="ft-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="kit-desc" /></div>
              <div>
                <label className="ft-label">Marketing Ribbon (optional)</label>
                <select
                  className="ft-input"
                  value={RIBBON_PRESETS.includes(form.ribbon_text) ? form.ribbon_text : (form.ribbon_text ? "__custom" : "")}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === "") setForm({ ...form, ribbon_text: "" });
                    else if (v === "__custom") setForm({ ...form, ribbon_text: form.ribbon_text && !RIBBON_PRESETS.includes(form.ribbon_text) ? form.ribbon_text : " " });
                    else setForm({ ...form, ribbon_text: v });
                  }}
                  data-testid="kit-ribbon-preset"
                >
                  <option value="">— No ribbon —</option>
                  {RIBBON_PRESETS.map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                  <option value="__custom">Custom…</option>
                </select>
                {form.ribbon_text && !RIBBON_PRESETS.includes(form.ribbon_text) && (
                  <input
                    className="ft-input mt-2"
                    placeholder="Custom ribbon label"
                    value={form.ribbon_text.trim() === "" ? "" : form.ribbon_text}
                    onChange={(e) => setForm({ ...form, ribbon_text: e.target.value })}
                    data-testid="kit-ribbon-custom"
                    maxLength={40}
                    autoFocus
                  />
                )}
                <div className="text-xs text-[var(--ft-muted)] mt-1">Displayed as a small gold ribbon above the kit pill on the public Sample Request Center.</div>
              </div>
              <div>
                <label className="ft-label">Products in kit* ({form.product_slugs.length} selected)</label>
                <div className="grid md:grid-cols-2 gap-2 mt-2">
                  {products.map((p) => (
                    <label key={p.slug} className={`flex items-center gap-2 text-sm cursor-pointer p-2 rounded-lg border transition-all ${form.product_slugs.includes(p.slug) ? "border-[var(--ft-gold)] bg-[var(--ft-gold)]/8" : "border-[var(--ft-border)]"}`} data-testid={`kit-product-${p.slug}`}>
                      <input type="checkbox" checked={form.product_slugs.includes(p.slug)} onChange={() => toggleProduct(p.slug)} />
                      <div>
                        <div className="font-medium">{p.name}</div>
                        <div className="text-[0.65rem] text-[var(--ft-muted)]">{p.status}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="ft-label">Status</label><select className="ft-input" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}><option value="active">Active</option><option value="inactive">Inactive</option></select></div>
                <div><label className="ft-label">Order</label><input type="number" className="ft-input" value={form.display_order} onChange={(e) => setForm({ ...form, display_order: parseInt(e.target.value, 10) || 0 })} /></div>
              </div>
              <div className="flex justify-end gap-2 pt-4 border-t border-[var(--ft-border)]">
                <button type="button" onClick={() => { setEditing(null); setShowNew(false); }} className="ft-btn-ghost">Cancel</button>
                <button type="submit" className="ft-btn-primary" data-testid="kit-save">Save Kit</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminSampleKits;
